import React, { useState, useEffect } from 'react';
import { db, auth } from './firebase';
import { GoogleAuthProvider, signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth';
import {
  collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, query, orderBy, where
} from 'firebase/firestore';

import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

import {
  PanelLeft, RotateCw, ChevronLeft, ChevronRight, Book, Plus, Save,
  AlertCircle, LayoutGrid, Trash2, Search, LogOut, Loader2, BookOpen, List
} from 'lucide-react';

const COLORS = ['#0088cc', '#228b22', '#f1c40f', '#e91e63', '#795548', '#f44336', '#9c27b0', '#607d8b'];

export default function App() {
  const [notebookLayout, setNotebookLayout] = useState('grid'); // 'grid' or 'list'

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [notebooks, setNotebooks] = useState([]);
  const [notes, setNotes] = useState([]);
  const [selectedNotebook, setSelectedNotebook] = useState(null);
  const [selectedNote, setSelectedNote] = useState(null);
  const [view, setView] = useState('all');
  const [searchTerm, setSearchTerm] = useState("");

  const [editContent, setEditContent] = useState("");
  const [isDirty, setIsDirty] = useState(false);

  const [isSidebarOpen, setSidebarOpen] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [newNbName, setNewNbName] = useState("");
  const [newNbColor, setNewNbColor] = useState(COLORS[0]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      if (u) fetchData(u.uid);
      else setLoading(false);
    });
    return unsub;
  }, []);

  const fetchData = async (uid) => {
    setLoading(true);
    console.log("Fetching data for UID:", uid);
    try {
      // Temporary: Removed orderBy to test if indexes are the problem
      const qNotes = query(collection(db, "notes"), where("userId", "==", uid));
      const nSnap = await getDocs(qNotes);
      console.log("Notes found:", nSnap.docs.length);
      setNotes(nSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      const qNb = query(collection(db, "notebooks"), where("userId", "==", uid));
      const nbSnap = await getDocs(qNb);
      setNotebooks(nbSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error("Firebase Fetch Error:", e);
    }
    setLoading(false);
  };

  // Helper to clean HTML for the list view
  const getPlainText = (html) => {
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    let text = tmp.textContent || tmp.innerText || "";
    // Remove &nbsp; and multiple spaces
    return text.replace(/\u00a0/g, " ").replace(/\s+/g, " ").trim();
  };

  const handleCreateNote = async () => {
    if (isDirty && !window.confirm("Discard unsaved changes?")) return;
    setActionLoading(true);
    try {
      const initialHTML = "<h1>New Note</h1><p><br></p>";
      const newNoteData = {
        userId: user.uid,
        title: "New Note",
        content: initialHTML,
        preview: "No additional text",
        notebookId: selectedNotebook ? selectedNotebook.id : "uncategorized",
        updatedAt: serverTimestamp()
      };
      const docRef = await addDoc(collection(db, "notes"), newNoteData);
      const created = { id: docRef.id, ...newNoteData };
      setNotes([created, ...notes]);
      setSelectedNote(created);
      setEditContent(initialHTML);
      setIsDirty(false);
      setView('all');
    } catch (e) { alert(e.message); }
    setActionLoading(false);
  };

  const handleSave = async () => {
    if (!selectedNote || actionLoading) return;
    setActionLoading(true);
    try {
      const noteRef = doc(db, "notes", selectedNote.id);

      // Get cleaned lines of text from the HTML editor
      const lines = getPlainTextLines(editContent);

      // 1st line is always the Title
      const title = lines[0] || "Untitled Note";

      // Remaining lines form the Preview (cleaned from HTML entities like &nbsp;)
      const preview = lines.slice(1).join(" ")
        .replace(/\u00a0/g, " ") // Replace &nbsp; with standard space
        .substring(0, 60) || "No additional text";

      const updated = {
        title: title,
        content: editContent,
        preview: preview,
        updatedAt: serverTimestamp()
      };

      await updateDoc(noteRef, updated);

      // Sync local state
      setNotes(notes.map(n => n.id === selectedNote.id ? { ...n, ...updated } : n));
      setSelectedNote({ ...selectedNote, ...updated });
      setIsDirty(false);
    } catch (e) {
      alert("Save failed: " + e.message);
    }
    setActionLoading(false);
  };

  const getPlainTextLines = (html) => {
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;

    // Select all block-level elements that contain text
    const blocks = tmp.querySelectorAll('h1, h2, h3, p, li, div');

    // Extract the text from each block and filter out empty ones
    const lines = Array.from(blocks)
      .map(el => el.textContent.trim())
      .filter(text => text.length > 0);

    // If the editor is empty but has raw text nodes outside tags
    if (lines.length === 0) {
      const fallbackText = tmp.textContent.trim();
      return fallbackText ? [fallbackText] : [];
    }

    return lines;
  };
  const handleCreateNotebook = async () => {
    if (!newNbName || actionLoading) return;
    setActionLoading(true);
    try {
      await addDoc(collection(db, "notebooks"), {
        userId: user.uid,
        name: newNbName,
        color: newNbColor,
        createdAt: serverTimestamp()
      });
      fetchData(user.uid); // Refresh
      setNewNbName("");
      setShowModal(false);
    } catch (e) { alert(e.message); }
    setActionLoading(false);
  };

  const handleDeleteNotebook = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("Delete notebook?")) return;
    await deleteDoc(doc(db, "notebooks", id));
    setNotebooks(notebooks.filter(nb => nb.id !== id));
  };
  const handleDeleteNote = async () => {
    if (!selectedNote || actionLoading) return;

    // Safety check
    if (!window.confirm("Permanently delete this note? This action cannot be undone.")) return;

    setActionLoading(true);
    try {
      // 1. Delete from Firebase
      await deleteDoc(doc(db, "notes", selectedNote.id));

      // 2. Remove from local state
      setNotes(notes.filter(n => n.id !== selectedNote.id));

      // 3. Clear editor and selection
      setSelectedNote(null);
      setEditContent("");
      setIsDirty(false);
    } catch (e) {
      alert("Delete failed: " + e.message);
    }
    setActionLoading(false);
  };
  if (loading) return (
    <div className="h-screen bg-[#121212] flex flex-col items-center justify-center text-white font-sans">
      <Loader2 className="animate-spin mb-4 text-blue-500" size={48} />
      <p className="opacity-40 text-xs tracking-widest uppercase">Syncing...</p>
    </div>
  );

  if (!user) return (
    <div className="h-screen bg-[#0a0a0a] flex items-center justify-center p-6 font-sans">
      <div className="bg-[#181818] p-10 rounded-3xl border border-white/5 text-center max-w-sm w-full shadow-2xl">
        <img src="/images/ic_launcher.png" className="w-20 mx-auto mb-5" alt="Logo" />
        <h1 className="text-2xl font-bold text-white mb-8">NoteX Cloud</h1>
        <button onClick={() => signInWithPopup(auth, new GoogleAuthProvider())} className="w-full bg-white text-black py-4 rounded-xl font-bold flex items-center justify-center gap-3 hover:bg-gray-200 transition-all">
          <img src="/images/logo-g.png" className="w-5" alt="g" /> Sign in with Google
        </button>
      </div>
    </div>
  );

  const filteredNotes = notes.filter(n => {
    const matchesNotebook = selectedNotebook ? n.notebookId === selectedNotebook.id : true;
    const matchesSearch = n.title.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesNotebook && matchesSearch;
  });

  return (
    <div className="flex h-screen bg-[#121212] text-gray-300 overflow-hidden text-sm font-sans">

      {/* SIDEBAR */}
      {isSidebarOpen && (
        <aside className="w-64 bg-[#1e1e1e] border-r border-white/5 flex flex-col shrink-0 shadow-xl">
          <div className="p-4 flex-1 overflow-y-auto">
            <div onClick={() => { setView('all'); setSelectedNotebook(null); }} className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all ${view === 'all' && !selectedNotebook ? 'bg-blue-600 text-white font-bold' : 'hover:bg-white/5'}`}>
              <Book size={18} /> All Notes
            </div>
            <div className="pt-6">
              <div className="flex justify-between items-center px-3 py-2 text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-2">
                {/* Wrap the text in a clickable span */}
                <span
                  onClick={() => {
                    setView('notebooks');
                    setSelectedNotebook(null); // Clear notebook selection to show all books
                  }}
                  className="cursor-pointer hover:text-white transition-colors"
                >
                  Notebooks
                </span>

                <Plus
                  size={14}
                  className="cursor-pointer hover:text-white"
                  onClick={() => setShowModal(true)}
                />
              </div>
              {notebooks.map(nb => (
                <div key={nb.id} onClick={() => { setSelectedNotebook(nb); setView('all'); }} className={`flex items-center group gap-3 p-3 rounded-xl cursor-pointer mt-1 transition-all ${selectedNotebook?.id === nb.id ? 'bg-white/10 text-white border-l-4 border-blue-500' : 'hover:bg-white/5'}`}>
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: nb.color }}></div>
                  <span className="truncate flex-1">{nb.name}</span>
                  <Trash2 size={12} className="opacity-0 group-hover:opacity-40 hover:opacity-100 text-red-400" onClick={(e) => handleDeleteNotebook(e, nb.id)} />
                </div>
              ))}
            </div>
          </div>
          <div className="p-4 border-t border-white/5">
            <button onClick={() => signOut(auth)} className="flex items-center gap-3 w-full p-2.5 hover:bg-red-500/10 text-red-500 rounded-xl transition-all font-bold"><LogOut size={16} /> Logout</button>
          </div>
        </aside>
      )}

      {/* MAIN */}
      <main className="flex-1 flex flex-col min-w-0 bg-[#121212]">
        <header className="h-16 bg-[#1e1e1e] border-b border-white/5 flex items-center justify-between px-6 shrink-0 z-10">
          <div className="flex items-center gap-6 flex-1">
            <button onClick={() => setSidebarOpen(!isSidebarOpen)} className="hover:text-white"><PanelLeft size={18} /></button>
            <button onClick={() => fetchData(user.uid)} className="hover:text-white transition-all"><RotateCw size={18} /></button>
            <div className="relative max-w-sm w-full hidden md:block">
              <Search size={14} className="absolute left-3.5 top-3 text-gray-500" />
              <input className="w-full bg-[#2a2a2a] border-none rounded-xl pl-11 pr-4 py-2.5 outline-none text-xs focus:ring-1 ring-blue-500" placeholder="Search notes..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            </div>
          </div>
          <div className="flex items-center gap-4 ">
            {/* Unsaved indicator */}
            {isDirty && (
              <div className="text-orange-400 text-[10px] font-bold uppercase animate-pulse">
                <AlertCircle size={12} className="inline mr-1" /> Unsaved
              </div>
            )}

            {/* NEW DELETE NOTE BUTTON (Visible only when a note is selected) */}
            {selectedNote && (
              <button
                onClick={handleDeleteNote}
                disabled={actionLoading}
                title="Delete Note"
                className="p-2.5 rounded-xl bg-red-500/10 hover:bg-red-500 text-red-500 hover:text-white transition-all active:scale-95 disabled:opacity-20"
              >
                <Trash2 size={18} />
              </button>
            )}

            {/* SAVE BUTTON */}
            <button
              onClick={handleSave}
              disabled={!isDirty || actionLoading}
              className={`px-5 py-2 rounded-xl font-bold text-xs flex items-center gap-2 transition-all ${isDirty ? 'bg-green-600 text-white shadow-lg shadow-green-900/20 active:scale-95' : 'bg-white/5 text-gray-600'}`}
            >
              {actionLoading ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {actionLoading ? "Saving..." : "Save"}
            </button>

            {/* NEW NOTE BUTTON */}
            <button
              onClick={handleCreateNote}
              disabled={actionLoading}
              className="px-5 py-2 rounded-xl font-bold text-xs bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-900/20 active:scale-95"
            >
              New Note
            </button>
          </div>
        </header>

        <div className="flex flex-1 overflow-hidden">
          {view === 'all' && (
            <div className="w-80 border-r border-white/5 bg-[#181818] overflow-y-auto shrink-0 flex flex-col">
              <div className="p-5 border-b border-white/5 text-[10px] font-bold uppercase tracking-widest text-gray-500">{selectedNotebook ? selectedNotebook.name : "My Cloud"}</div>
              {filteredNotes.map(note => (
                <div key={note.id} onClick={() => { setSelectedNote(note); setEditContent(note.content); setIsDirty(false); }} className={`p-5 border-b border-white/5 cursor-pointer transition-all ${selectedNote?.id === note.id ? 'bg-[#252525] border-l-4 border-l-blue-500' : 'hover:bg-white/5'}`}>
                  <h3 className="font-bold text-white truncate text-sm mb-1">{note.title}</h3>
                  <p className="text-xs text-gray-500 truncate opacity-60 leading-relaxed">{note.preview}</p>
                </div>
              ))}
            </div>
          )}

          <div className="flex-1 overflow-hidden">
            {view === 'notebooks' ? (
              /* --- NOTEBOOKS VIEW --- */
              <div className="p-12 overflow-y-auto h-full">
                <div className="flex justify-between items-center mb-10 max-w-6xl mx-auto">
                  <div className="flex items-center gap-4">
                    <h1 className="text-3xl font-bold text-white tracking-tight">Notebooks</h1>
                    <span className="bg-white/5 px-3 py-1 rounded-full text-xs text-gray-500 font-bold">{notebooks.length}</span>
                  </div>

                  {/* VIEW TOGGLE SWITCH */}
                  <div className="flex bg-[#1e1e1e] p-1 rounded-xl border border-white/5 shadow-xl">
                    <button
                      onClick={() => setNotebookLayout('grid')}
                      className={`p-2 rounded-lg transition-all ${notebookLayout === 'grid' ? 'bg-blue-600 text-white' : 'text-gray-500 hover:text-gray-300'}`}
                    >
                      <LayoutGrid size={18} />
                    </button>
                    <button
                      onClick={() => setNotebookLayout('list')}
                      className={`p-2 rounded-lg transition-all ${notebookLayout === 'list' ? 'bg-blue-600 text-white' : 'text-gray-500 hover:text-gray-300'}`}
                    >
                      <List size={18} />
                    </button>
                  </div>
                </div>

                <div className="max-w-6xl mx-auto">
                  {notebookLayout === 'grid' ? (
                    /* --- GRID LAYOUT --- */
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                      {notebooks.map(nb => (
                        <div key={nb.id} onClick={() => { setSelectedNotebook(nb); setView('all'); }} className="group cursor-pointer relative transition-all active:scale-95">
                          <div className="aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl relative border border-white/5" style={{ backgroundColor: nb.color }}>
                            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent"></div>
                            <div className="absolute bottom-0 p-6">
                              <h2 className="text-xl font-bold text-white leading-tight drop-shadow-md">{nb.name}</h2>
                              <p className="text-[10px] text-white/40 uppercase mt-2 font-bold tracking-widest">Notebook</p>
                            </div>
                            <button
                              onClick={(e) => handleDeleteNotebook(e, nb.id)}
                              className="absolute top-4 right-4 p-2.5 bg-black/40 rounded-xl opacity-0 group-hover:opacity-100 hover:bg-red-600 transition-all backdrop-blur-md"
                            >
                              <Trash2 size={14} className="text-white" />
                            </button>
                          </div>
                        </div>
                      ))}
                      <div onClick={() => setShowModal(true)} className="aspect-[3/4] rounded-2xl border-2 border-dashed border-white/5 flex flex-col items-center justify-center cursor-pointer hover:border-blue-500 text-gray-700 hover:text-blue-500 transition-all bg-white/[0.02]">
                        <Plus size={32} />
                        <span className="mt-2 font-bold text-xs tracking-widest uppercase">New Notebook</span>
                      </div>
                    </div>
                  ) : (
                    /* --- LIST LAYOUT --- */
                    <div className="space-y-2">
                      <div className="grid grid-cols-12 px-6 py-3 text-[10px] font-bold text-gray-600 uppercase tracking-widest border-b border-white/5">
                        <div className="col-span-8">Name</div>
                        <div className="col-span-3 text-right">Updated</div>
                        <div className="col-span-1"></div>
                      </div>
                      {notebooks.map(nb => (
                        <div
                          key={nb.id}
                          onClick={() => { setSelectedNotebook(nb); setView('all'); }}
                          className="grid grid-cols-12 items-center px-6 py-4 bg-[#1e1e1e]/50 hover:bg-[#1e1e1e] border border-white/5 rounded-2xl cursor-pointer group transition-all"
                        >
                          <div className="col-span-8 flex items-center gap-4">
                            <div className="w-8 h-10 rounded-lg shadow-lg shrink-0" style={{ backgroundColor: nb.color }}></div>
                            <span className="font-bold text-white text-base">{nb.name}</span>
                          </div>
                          <div className="col-span-3 text-right text-xs text-gray-500 font-medium">
                            Just now
                          </div>
                          <div className="col-span-1 flex justify-end">
                            <button
                              onClick={(e) => handleDeleteNotebook(e, nb.id)}
                              className="p-2 text-gray-600 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      ))}
                      <button
                        onClick={() => setShowModal(true)}
                        className="w-full py-4 border-2 border-dashed border-white/5 rounded-2xl flex items-center justify-center gap-3 text-gray-600 hover:text-blue-500 hover:border-blue-500 transition-all group"
                      >
                        <Plus size={18} />
                        <span className="font-bold uppercase tracking-widest text-xs">Create New Notebook</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="h-full flex flex-col relative custom-editor-container">
                {selectedNote ? (
                  <ReactQuill
                    theme="snow"
                    className="h-full flex flex-col"
                    value={editContent}
                    onChange={(content) => { setEditContent(content); setIsDirty(true); }}
                    placeholder="Title starts on the first line..."
                    modules={{
                      toolbar: [
                        [{ 'header': [1, 2, false] }],
                        ['bold', 'italic', 'underline', 'strike'],
                        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                        ['image', 'code-block', 'link'],
                        ['clean']
                      ]
                    }}
                  />
                ) : (
                  <div className="h-full flex flex-col items-center justify-center opacity-5"><BookOpen size={180} /></div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* CREATE NOTEBOOK MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-6 backdrop-blur-md">
          <div className="bg-[#1e1e1e] w-full max-w-[420px] rounded-3xl p-10 border border-white/10 shadow-2xl">
            <h2 className="text-xl font-bold text-center mb-8 text-white">New Notebook</h2>
            <div className="space-y-8">
              <input className="w-full bg-[#2a2a2a] border-none rounded-2xl px-5 py-4 outline-none focus:ring-2 ring-blue-500 text-white" placeholder="Name your notebook" value={newNbName} onChange={(e) => setNewNbName(e.target.value)} />
              <div className="grid grid-cols-4 gap-4">
                {COLORS.map(c => (
                  <div key={c} onClick={() => setNewNbColor(c)} className={`h-12 rounded-xl cursor-pointer transition-all border-4 ${newNbColor === c ? 'border-white scale-110 shadow-xl' : 'border-transparent opacity-50'}`} style={{ backgroundColor: c }}></div>
                ))}
              </div>
            </div>
            <div className="mt-12 flex gap-4">
              <button onClick={() => setShowModal(false)} className="flex-1 py-4 font-bold text-gray-500 hover:text-white">Cancel</button>
              <button onClick={handleCreateNotebook} disabled={actionLoading} className="flex-1 bg-blue-600 py-4 rounded-2xl font-bold text-white hover:bg-blue-700 flex items-center justify-center gap-2 transition-all">
                {actionLoading && <Loader2 size={16} className="animate-spin" />} {actionLoading ? "Creating..." : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}